import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'sonner';
import api from '@/utils/api';
import { getUser } from '@/utils/auth';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  FileSpreadsheet,
  Clock,
  Table2,
  Search,
  RotateCcw,
  CheckCircle2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

function toDateInputValue(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function defaultDateRange() {
  const today = new Date();
  const lastWeek = new Date();
  lastWeek.setDate(today.getDate() - 7);
  return {
    fromDate: toDateInputValue(lastWeek),
    toDate: toDateInputValue(today),
  };
}

function formatDurasi(hoursWorked) {
  if (hoursWorked === null || hoursWorked === undefined) return '-';
  const totalMinutes = Math.round(hoursWorked * 60);
  const hh = String(Math.floor(totalMinutes / 60)).padStart(2, '0');
  const mm = String(totalMinutes % 60).padStart(2, '0');
  return `${hh}:${mm}`;
}

function getInitials(name) {
  if (!name) return 'U';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function Reports() {
  const user = getUser();
  const canFilterByEmployee = user?.role === 'admin' || user?.role === 'security_manager';

  const [checkIns, setCheckIns] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [{ fromDate, toDate }, setDateRange] = useState(defaultDateRange);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [exportingExcel, setExportingExcel] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    if (canFilterByEmployee) {
      fetchEmployees();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchCheckIns(selectedEmployee, fromDate, toDate);
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmployee, fromDate, toDate]);

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/checkins/employees');
      setEmployees(response.data);
    } catch (error) {
      console.error('Gagal memuat daftar karyawan', error);
    }
  };

  const fetchCheckIns = async (employeeFilter, from, to) => {
    if (from && to && from > to) {
      toast.error('Tanggal mulai tidak boleh melebihi tanggal selesai');
      return;
    }
    setLoading(true);
    try {
      const params = {};
      if (employeeFilter && employeeFilter !== 'all') {
        params.user_id = employeeFilter;
      }
      if (from) params.from_date = from;
      if (to) params.to_date = to;
      const response = await api.get('/checkins', { params });
      setCheckIns(response.data);
    } catch (error) {
      toast.error('Gagal memuat data riwayat presensi');
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    setExportingExcel(true);
    try {
      const params = {};
      if (selectedEmployee && selectedEmployee !== 'all') {
        params.user_id = selectedEmployee;
      }
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (debouncedSearchTerm.trim()) params.search = debouncedSearchTerm.trim();

      const response = await api.get('/reports/checkins-table/export', {
        params,
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `report_checkin_${fromDate}_sd_${toDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Riwayat presensi berhasil diexport ke Excel!');
    } catch (error) {
      toast.error('Gagal export riwayat presensi ke Excel');
    } finally {
      setExportingExcel(false);
    }
  };

  const initialRange = useMemo(() => defaultDateRange(), []);

  // Quick Date Presets
  const applyPreset = (preset) => {
    const today = new Date();
    if (preset === 'today') {
      const d = toDateInputValue(today);
      setDateRange({ fromDate: d, toDate: d });
    } else if (preset === '7days') {
      const past = new Date();
      past.setDate(today.getDate() - 7);
      setDateRange({ fromDate: toDateInputValue(past), toDate: toDateInputValue(today) });
    } else if (preset === '30days') {
      const past = new Date();
      past.setDate(today.getDate() - 30);
      setDateRange({ fromDate: toDateInputValue(past), toDate: toDateInputValue(today) });
    } else if (preset === 'thisMonth') {
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      setDateRange({ fromDate: toDateInputValue(firstDay), toDate: toDateInputValue(today) });
    }
  };

  const handleResetFilters = () => {
    setDateRange(defaultDateRange());
    setSelectedEmployee('all');
    setSearchTerm('');
    setCurrentPage(1);
  };

  // Filtered checkins by search query (debounced)
  const filteredCheckIns = useMemo(() => {
    if (!debouncedSearchTerm.trim()) return checkIns;
    const term = debouncedSearchTerm.toLowerCase();
    return checkIns.filter(
      (c) =>
        (c.user_name && c.user_name.toLowerCase().includes(term)) ||
        (c.date && c.date.includes(term))
    );
  }, [checkIns, debouncedSearchTerm]);

  // Paginated check-ins
  const totalPages = Math.ceil(filteredCheckIns.length / pageSize) || 1;
  const paginatedCheckIns = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredCheckIns.slice(start, start + pageSize);
  }, [filteredCheckIns, currentPage, pageSize]);

  const isFiltered =
    selectedEmployee !== 'all' ||
    searchTerm !== '' ||
    fromDate !== initialRange.fromDate ||
    toDate !== initialRange.toDate;

  return (
    <div className="space-y-8">
      {/* Page Header — samain sama Dashboard/Timesheet */}
      <div className="pb-2 border-b border-slate-200/70 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold font-heading text-slate-900 tracking-tight" data-testid="reports-page-title">
            Laporan Presensi
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Rekap lengkap check-in, check-out, dan durasi kerja — siap diexport
          </p>
        </div>
        <Button
          onClick={handleExportExcel}
          disabled={exportingExcel}
          data-testid="export-report-table-excel-button"
          className="bg-[#1A3C34] hover:bg-[#142E27] text-white font-medium rounded-xl shadow-md transition-all duration-200 active:scale-[0.99]"
        >
          <FileSpreadsheet className="w-4 h-4 mr-2" />
          {exportingExcel ? 'Mengexport...' : 'Export Excel (.xlsx)'}
        </Button>
      </div>

      <Card className="p-6 bg-white border-slate-200/80 shadow-sm">
        {/* Table Header & Title */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-5 border-b border-slate-100 gap-2">
          <div>
            <h2 className="text-xl font-bold font-heading text-slate-900 flex items-center gap-2">
              <Table2 className="w-5 h-5 text-[#1A3C34]" />
              Riwayat Presensi
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Log lengkap kehadiran, waktu check in, check out, dan durasi kerja
            </p>
          </div>
          <div className="text-xs text-muted-foreground">
            Total data: <strong className="text-slate-900">{filteredCheckIns.length}</strong> entri
          </div>
        </div>

        {/* Filter Control Bar */}
        <div className="mt-6 space-y-4">
          {/* Presets and Search */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
            {/* Quick Date Presets */}
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-xs font-medium text-slate-500 mr-1">Preset:</span>
              <button
                type="button"
                onClick={() => applyPreset('today')}
                className="px-2.5 py-1 text-xs font-medium rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors"
              >
                Hari Ini
              </button>
              <button
                type="button"
                onClick={() => applyPreset('7days')}
                className="px-2.5 py-1 text-xs font-medium rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors"
              >
                7 Hari Terakhir
              </button>
              <button
                type="button"
                onClick={() => applyPreset('30days')}
                className="px-2.5 py-1 text-xs font-medium rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors"
              >
                30 Hari Terakhir
              </button>
              <button
                type="button"
                onClick={() => applyPreset('thisMonth')}
                className="px-2.5 py-1 text-xs font-medium rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors"
              >
                Bulan Ini
              </button>
            </div>

            {/* Real-time Search */}
            <div className="relative w-full lg:w-72">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                type="text"
                placeholder="Cari nama karyawan..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 bg-white border-slate-200 rounded-xl text-sm placeholder:text-slate-300 placeholder:font-normal focus-visible:ring-[#1A3C34] focus-visible:border-[#1A3C34]"
              />
            </div>
          </div>

          {/* Date Inputs & Employee Select */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-2">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={fromDate}
                  max={toDate}
                  onChange={(e) => setDateRange((prev) => ({ ...prev, fromDate: e.target.value }))}
                  className="w-[155px] bg-white border-slate-200 rounded-xl text-sm"
                  data-testid="report-table-from-date-input"
                />
                <span className="text-muted-foreground text-xs font-medium">s/d</span>
                <Input
                  type="date"
                  value={toDate}
                  min={fromDate}
                  onChange={(e) => setDateRange((prev) => ({ ...prev, toDate: e.target.value }))}
                  className="w-[155px] bg-white border-slate-200 rounded-xl text-sm"
                  data-testid="report-table-to-date-input"
                />
              </div>

              {canFilterByEmployee && (
                <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                  <SelectTrigger className="w-[190px] bg-white border-slate-200 rounded-xl text-sm" data-testid="report-table-employee-filter-select">
                    <SelectValue placeholder="Semua Karyawan" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua Karyawan</SelectItem>
                    {employees.map((emp) => (
                      <SelectItem key={emp.user_id} value={emp.user_id}>
                        {emp.user_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {isFiltered && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleResetFilters}
                className="text-xs text-slate-500 hover:text-slate-800 self-start sm:self-auto rounded-xl"
              >
                <RotateCcw className="w-3.5 h-3.5 mr-1" />
                Reset Filter
              </Button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="mt-6 rounded-2xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-semibold text-xs uppercase tracking-wider">
                  <th className="text-left py-3.5 px-4">Karyawan</th>
                  <th className="text-left py-3.5 px-4">Check In</th>
                  <th className="text-left py-3.5 px-4">Check Out</th>
                  <th className="text-center py-3.5 px-4">Status</th>
                  <th className="text-right py-3.5 px-4">Durasi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan={5} className="py-16 text-center text-muted-foreground">
                      <div className="flex flex-col items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-2"></div>
                        <p className="text-sm">Memuat data presensi...</p>
                      </div>
                    </td>
                  </tr>
                ) : paginatedCheckIns.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-16 text-center text-muted-foreground">
                      <div className="flex flex-col items-center justify-center">
                        <AlertCircle className="w-10 h-10 text-slate-300 mb-2" />
                        <p className="font-semibold text-slate-700">Tidak ada data ditemukan</p>
                        <p className="text-xs text-slate-500 mt-1 max-w-sm">
                          Coba ubah rentang tanggal atau pilih karyawan lain untuk melihat riwayat presensi.
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedCheckIns.map((checkin) => {
                    const isCompleted = Boolean(checkin.check_out_time);
                    return (
                      <tr
                        key={checkin.id}
                        className="hover:bg-[#1A3C34]/5 transition-colors"
                        data-testid={`report-table-row-${checkin.id}`}
                      >
                        {/* Employee */}
                        <td className="py-3 px-4 font-medium text-slate-900">
                          <div className="flex items-center gap-3">
                            <span>{checkin.user_name}</span>
                          </div>
                        </td>

                        {/* Check In */}
                        <td className="py-3 px-4 text-slate-600">
                          <div className="flex items-center gap-1.5">
                            <span>
                              {new Date(checkin.check_in_time).toLocaleString('id-ID', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </span>
                          </div>
                        </td>

                        {/* Check Out */}
                        <td className="py-3 px-4 text-slate-600">
                          {checkin.check_out_time ? (
                            <div className="flex items-center gap-1.5">
                              <span>
                                {new Date(checkin.check_out_time).toLocaleString('id-ID', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </span>
                            </div>
                          ) : (
                            <span className="text-amber-600 font-medium text-xs">Belum Check Out</span>
                          )}
                        </td>

                        {/* Status */}
                        <td className="py-3 px-4 text-center">
                          {isCompleted ? (
                            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs font-medium">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Selesai
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs font-medium">
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse mr-1.5"></span>
                              Sedang Bekerja
                            </Badge>
                          )}
                        </td>

                        {/* Durasi */}
                        <td className="py-3 px-4 text-right font-medium text-slate-800">
                          {formatDurasi(checkin.hours_worked)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {filteredCheckIns.length > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between p-4 bg-slate-50 border-t border-slate-200 gap-3">
              <p className="text-xs text-muted-foreground">
                Menampilkan {(currentPage - 1) * pageSize + 1} -{' '}
                {Math.min(currentPage * pageSize, filteredCheckIns.length)} dari{' '}
                {filteredCheckIns.length} data
              </p>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  className="h-8 px-2.5 text-xs bg-white rounded-xl border-slate-200"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Sebelumnya
                </Button>
                <span className="text-xs font-medium px-2 text-slate-700">
                  Hal {currentPage} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  className="h-8 px-2.5 text-xs bg-white rounded-xl border-slate-200"
                >
                  Berikutnya
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}